import React, { useMemo } from 'react';
import { Box } from '@wix/design-system';
import {
  useEssentials,
  useRouterContext,
  useServices,
  useSiteContext,
  QueryDecorator,
  useTranslation,
  useComponentLoadReporter,
} from '@wix/app-market-core';
import { useQueryClient } from '@tanstack/react-query';
import { RoutePath } from '@wix/app-market-core-types';
import type { HorizontalNavBarItem } from '@wix/app-market-components';
import {
  HorizontalNavBar as HorizontalNavBarComponent,
  HorizontalNavBarSkeleton,
  SearchBox,
} from '@wix/app-market-components';
import { SidebarService } from '../sidebar/sidebar-service';
import { getDynamicSidebarItems } from '../sidebar/sidebar-api';
import { ESidebarItemTypes } from '../sidebar/sidebar-constants';
import type { SidebarLink } from '../sidebar/types';
import { useSearchBoxActions } from '../search-box/hooks/search-box-actions';
import { horizontalNavBarComponentId } from './horizontal-nav-bar-constants';

/**
 * The horizontal navigation of the market: renders the sidebar's top-level
 * items as a pills bar with a search box, replacing the vertical sidebar
 * when the host sets `horizontalNav` on the site context.
 */
export function HorizontalNavBar() {
  const { biLogger, marketWatch } = useEssentials();
  const { t } = useTranslation();
  const services = useServices();
  const { route, router } = useRouterContext();
  const { metaSiteId, baseURL, languageCode } = useSiteContext();
  const queryClient = useQueryClient();

  const queryKey = horizontalNavBarComponentId;
  const reportLoadFinish = useComponentLoadReporter({
    componentId: horizontalNavBarComponentId,
    params: { route, metaSiteId, languageCode },
    deps: [queryKey],
  });

  if (route.payload?.hideMenu) {
    reportLoadFinish();
    return null;
  }

  return (
    <QueryDecorator
      queryFn={() =>
        getDynamicSidebarItems({
          services,
          queryClient,
          marketWatch,
          isMetaSiteId: !!metaSiteId,
        })
      }
      queryKey={queryKey}
      deps={[JSON.stringify(route)]}
    >
      {({ data: sections, isLoading }) => {
        if (isLoading) {
          return <HorizontalNavBarSkeleton />;
        }

        const sidebar = new SidebarService({
          sections,
          route,
          router,
          baseURL,
          t,
          biLogger,
          metaSiteId,
          horizontalNav: true,
        });

        return (
          <HorizontalNavBarContent
            sidebar={sidebar}
            showSearch={!!metaSiteId}
            onLoad={reportLoadFinish}
          />
        );
      }}
    </QueryDecorator>
  );
}

function HorizontalNavBarContent({
  sidebar,
  showSearch,
  onLoad,
}: {
  sidebar: SidebarService;
  showSearch: boolean;
  onLoad(): void;
}) {
  const { experiments } = useEssentials();
  const { languageCode } = useSiteContext();
  const { route } = useRouterContext();
  const { onSelect, onChange, onFocus, onEnterPressed } = useSearchBoxActions(
    'horizontal_nav',
    'search-bar-horizontal-nav',
  );
  useMemo(() => onLoad(), [languageCode]);

  const searchTerm =
    route.path === RoutePath.SEARCH_RESULTS ? route.payload?.query : undefined;

  const linkItems = sidebar.sidebarItems.filter(
    (item): item is SidebarLink => item.type === ESidebarItemTypes.LINK,
  );
  const navBarItems: HorizontalNavBarItem[] = linkItems.map(
    ({ key, label, href }) => ({ key, label, href }),
  );

  return (
    <HorizontalNavBarComponent
      items={navBarItems}
      selectedItemKey={sidebar.selectedItemKey}
      onItemClick={(_item, index) =>
        sidebar.onItemClick(linkItems[index], index)
      }
      sideContent={
        showSearch && (
          /* fixed width so the search does not resize on focus/typing */
          <Box width="213px">
            <SearchBox
              dataHook="search-box-horizontal-nav"
              size="small"
              border="round"
              /* wider than the input; anchored to the right, so the
                 dropdown opens to the left */
              dropdownWidth="380px"
              popoverPlacement="bottom-end"
              givenSearchTerm={searchTerm}
              onChange={onChange}
              onSelect={onSelect}
              onEnterPressed={onEnterPressed}
              onFocus={onFocus}
              languageCode={languageCode}
              experiments={experiments}
            />
          </Box>
        )
      }
    />
  );
}
